import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ImagePlus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const MAX_IMAGES = 5;

/**
 * Resize image to fit within maxSize bytes
 */
async function resizeImageToMaxSize(file, maxSize = MAX_FILE_SIZE) {
    return new Promise((resolve) => {
        const img = new Image();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        img.onload = () => {
            let { width, height } = img;
            let quality = 0.9;
            
            // Start with original dimensions
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);
            
            // Iteratively reduce quality/size until under maxSize
            const tryResize = () => {
                canvas.toBlob((blob) => {
                    if (blob.size <= maxSize || quality < 0.1) {
                        // Convert blob to file
                        const resizedFile = new File([blob], file.name, {
                            type: 'image/jpeg',
                            lastModified: Date.now()
                        });
                        resolve(resizedFile);
                    } else {
                        // Reduce quality or dimensions
                        if (quality > 0.3) {
                            quality -= 0.1;
                        } else {
                            // Reduce dimensions
                            width = Math.floor(width * 0.8);
                            height = Math.floor(height * 0.8);
                            canvas.width = width;
                            canvas.height = height;
                            ctx.drawImage(img, 0, 0, width, height);
                        }
                        tryResize();
                    }
                }, 'image/jpeg', quality);
            };
            
            tryResize();
        };
        
        img.src = URL.createObjectURL(file);
    });
}

export const ReportImageUpload = ({ images, setImages, uploading, setUploading }) => {
    const fileInputRef = useRef(null);
    const [processingCount, setProcessingCount] = useState(0);
    
    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        
        // Check max images limit
        if (images.length + files.length > MAX_IMAGES) {
            toast.error(`Poți atașa maxim ${MAX_IMAGES} imagini`);
            return;
        }
        
        setUploading(true);
        setProcessingCount(files.length);
        
        const newImages = [];
        
        for (const file of files) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error(`${file.name} nu este o imagine validă`);
                continue;
            }
            
            try {
                let processedFile = file;
                
                // Resize if larger than 2MB
                if (file.size > MAX_FILE_SIZE) {
                    toast.info(`Se redimensionează ${file.name}...`);
                    processedFile = await resizeImageToMaxSize(file, MAX_FILE_SIZE);
                }
                
                // Create preview URL and store file
                const preview = URL.createObjectURL(processedFile);
                newImages.push({
                    file: processedFile,
                    preview,
                    name: file.name,
                    size: processedFile.size
                });
                
            } catch (error) {
                console.error('Error processing image:', error);
                toast.error(`Eroare la procesarea ${file.name}`);
            }
            
            setProcessingCount(prev => prev - 1);
        }
        
        setImages(prev => [...prev, ...newImages]);
        setUploading(false);
        setProcessingCount(0);
        
        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    
    const removeImage = (index) => {
        setImages(prev => {
            const newImages = [...prev];
            // Revoke object URL to free memory
            URL.revokeObjectURL(newImages[index].preview);
            newImages.splice(index, 1);
            return newImages;
        });
    };
    
    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                    Imagini atașate ({images.length}/{MAX_IMAGES})
                </span>
                {images.length < MAX_IMAGES && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="rounded-full"
                    >
                        {uploading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Se procesează... ({processingCount})
                            </>
                        ) : (
                            <>
                                <ImagePlus className="w-4 h-4 mr-2" />
                                Adaugă imagini
                            </>
                        )}
                    </Button>
                )}
            </div>
            
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileSelect}
            />
            
            {/* Image previews */}
            {images.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {images.map((img, index) => (
                        <div key={index} className="relative group aspect-square">
                            <img
                                src={img.preview}
                                alt={`Atașament ${index + 1}`}
                                className="w-full h-full object-cover rounded-lg border"
                            />
                            <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600"
                            >
                                <X className="w-4 h-4" />
                            </button>
                            <span className="absolute bottom-1 left-1 right-1 text-[10px] text-white bg-black/50 rounded px-1 truncate">
                                {(img.size / 1024).toFixed(0)} KB
                            </span>
                        </div>
                    ))}
                </div>
            )}
            
            <p className="text-xs text-muted-foreground">
                Imaginile mai mari de 2MB vor fi redimensionate automat. Maxim {MAX_IMAGES} imagini.
            </p>
        </div>
    );
};

export default ReportImageUpload;
